/**
 * Wire contract for the token/cost reports (`GET /api/admin/usage/stats`).
 *
 * Lives here rather than beside the service for the same reason the agent admin
 * request schemas do: the browser has to name the same shapes the server
 * answers with, and it cannot import from `apps/server`.
 */
import { Schema } from "effect";

import { NonNegativeInt, ThreadId, TrimmedNonEmptyString, UserId } from "./baseSchemas";
import { ProviderKind } from "./orchestration";
import { TurnUsageKind } from "./tokenUsage";

/**
 * The axis the caller wants the ledger collapsed onto.
 *
 * `kind` answers "what were the tokens spent on" — conversation turns versus the
 * short-form writing the server does on its own behalf (see `TurnUsageKind`).
 * It is the one axis with a bounded, known set of values, which makes it the
 * only one where an empty bucket is itself information.
 */
export const UsageStatsGroupBy = Schema.Literals([
  "model",
  "user",
  "thread",
  "agent",
  "day",
  "kind",
]);
export type UsageStatsGroupBy = typeof UsageStatsGroupBy.Type;

export const USAGE_STATS_GROUP_BY_VALUES = [
  "model",
  "user",
  "thread",
  "agent",
  "day",
  "kind",
] as const;

/**
 * A date-only bound (`YYYY-MM-DD`), inclusive at both ends.
 *
 * Date-only on purpose: usage is reported by day, and a full timestamp would
 * invite a client to send a local-time instant that silently moves turns across
 * the day boundary the reports group on.
 */
export const UsageStatsDate = Schema.String.check(Schema.isPattern(/^\d{4}-\d{2}-\d{2}$/));

/**
 * Query-string shape. Every field but `groupBy` is an optional filter, and they
 * combine with AND.
 */
export const UsageStatsQuery = Schema.Struct({
  groupBy: UsageStatsGroupBy,
  from: Schema.optional(UsageStatsDate),
  to: Schema.optional(UsageStatsDate),
  /**
   * Matches the thread AND every thread delegated from it, at any depth: a
   * subagent's tokens are part of what its parent thread cost.
   */
  threadId: Schema.optional(ThreadId),
  userId: Schema.optional(UserId),
  model: Schema.optional(TrimmedNonEmptyString),
  provider: Schema.optional(ProviderKind),
  /**
   * Narrow to one kind of spend. Combined with `groupBy: "user"` this is what
   * answers "who is generating all these commit messages", which the ledger
   * could not express while every row was a conversation turn.
   */
  kind: Schema.optional(TurnUsageKind),
});
export type UsageStatsQuery = typeof UsageStatsQuery.Type;

/**
 * Summed counters. The subset relationship documented on `TurnModelUsage`
 * survives aggregation untouched: `cachedInputTokens` and
 * `cacheCreationInputTokens` are parts of `inputTokens`, and
 * `reasoningOutputTokens` is part of `outputTokens`. Adding them together
 * double-counts; `totalTokens` is the only figure meant to be read as a whole,
 * and it equals `inputTokens + outputTokens`.
 */
export const UsageStatsTotals = Schema.Struct({
  inputTokens: NonNegativeInt,
  cachedInputTokens: NonNegativeInt,
  cacheCreationInputTokens: NonNegativeInt,
  outputTokens: NonNegativeInt,
  reasoningOutputTokens: NonNegativeInt,
  totalTokens: NonNegativeInt,
  /**
   * Null when not one row in the group carried a price. A partial sum is still
   * returned when only some did — Codex reports no cost — so a mixed group's
   * figure is a floor, never an overstatement. Render it as "at least", or as
   * unknown when null; never as zero.
   */
  costUsd: Schema.NullOr(Schema.Number),
  /** How many `(turn, model)` rows the group folded. */
  entryCount: NonNegativeInt,
  /** Distinct turns behind those rows; lower than `entryCount` on Claude. */
  turnCount: NonNegativeInt,
});
export type UsageStatsTotals = typeof UsageStatsTotals.Type;

export const UsageStatsRow = Schema.Struct({
  /**
   * The group's identity on the requested axis: a model slug, a user id, a
   * thread id, an agent id, or a `YYYY-MM-DD` day. Null where the axis itself
   * is unknown — usage with no attributable user, or a thread running no
   * managed agent — which is a real bucket to render, not a row to hide.
   */
  key: Schema.NullOr(Schema.String),
  totals: UsageStatsTotals,
});
export type UsageStatsRow = typeof UsageStatsRow.Type;

export const UsageStatsResult = Schema.Struct({
  groupBy: UsageStatsGroupBy,
  /**
   * Ordered heaviest first by `totalTokens`, except for `groupBy: "day"`, which
   * comes back chronologically so it can be charted as a series without
   * re-sorting.
   */
  rows: Schema.Array(UsageStatsRow),
  /**
   * The same sums over everything the filter matched, ungrouped. Not a fold of
   * `rows`: `turnCount` is a distinct count, and a turn spanning two models is
   * counted once here and once in each of its groups.
   */
  totals: UsageStatsTotals,
});
export type UsageStatsResult = typeof UsageStatsResult.Type;
